import objectPath from 'object-path'
import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Form, Button, InputGroup } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import httpClient from 'utility/httpClient.util'
import medicalStatisticStyles from '../MedicalStatistic/MedicalStatisticDetailModal.module.css'

import CompareModal from './CompareModal'
import './style.scss'

const DRG_RESULT_MAPPING = {
  MDC: 'mdc',
  DRG: 'drg',
  RW: 'rw',
  OT: 'ot',
  ADJRW: 'adjRw',
  ActLOS: 'los',
  Warn: 'warn',
  Err: 'err',
}

const DRG_RESULT_FIELDS = Object.values(DRG_RESULT_MAPPING)

const normalizeDrgResultValue = (value) => {
  if (value === null || value === undefined) return ''

  return String(value)
}

const isSameDrgResult = (current = {}, next = {}) =>
  DRG_RESULT_FIELDS.every(
    (field) =>
      normalizeDrgResultValue(current[field]) ===
      normalizeDrgResultValue(next[field]),
  )

const getBooleanDesignerValue = (value, defaultValue = false) => {
  if (value === null || value === undefined || value === '') {
    return defaultValue
  }

  if (typeof value === 'boolean') return value

  if (typeof value === 'string') {
    return ['true', '1', 'yes'].includes(value.toLowerCase())
  }

  return Boolean(value)
}

export const getErrorDesc = (error) => {
  if (error > 0) {
    const errorDescriptions = {
      1: 'No principal diagnosis',
      2: 'Invalid principal diagnosis',
      3: 'Unacceptable principal diagnosis',
      4: 'Principal diagnosis not valid for age',
      5: 'Principal diagnosis not valid for sex',
      6: 'Age error',
      7: 'Ungroupable due to sex error',
      8: 'Ungroupable due to discharge type error',
      9: 'Length of stay error',
      10: 'Ungroupable due to admission weight error',
    }

    return errorDescriptions[error]
      ? errorDescriptions[error]
      : 'Unknown Error ' + error
  } else {
    return ''
  }
}

export const getWarningDesc = (error) => {
  if (error > 0) {
    const errorDescriptions = {
      1: 'SDx is not valid or the same as PDx',
      2: 'SDx is not suitable for age',
      4: 'SDx is not suitable for sex or is a code for one gender without sex data',
      8: 'Proc is not valid or the same as itself',
      16: 'Proc is not suitable for sex or is a code for one gender without sex data',
      32: 'No sex data or using a code other than the specified ones',
      64: 'No discharge type or using a code other than the specified ones',
      128: 'No admission or discharge date and/or time or incorrect date and/or time',
      256: 'No admission or discharge date and/or time or incorrect date and/or time',
    }
    return errorDescriptions[error]
      ? errorDescriptions[error]
      : 'Unknown Warning ' + error
  } else {
    return ''
  }
}

const DrgCalculator = ({ designerData = {}, designerKey, medicalStatisticLayout = false, patientNumber = {} }) => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const getDesignerValue = (key, defaultValue) => {
    if (designerKey) {
      const nestedValue = objectPath.get(designerData, `${designerKey}.${key}`)
      if (nestedValue !== undefined) return nestedValue
    }

    return objectPath.get(designerData, key, defaultValue)
  }
  const hn = patientNumber.hn || queryParams.get('hn') || getDesignerValue('hn')
  const vn = patientNumber.vn || queryParams.get('vn') || getDesignerValue('vn')
  const an = patientNumber.an || queryParams.get('an') || getDesignerValue('an')
  const isShowSaveButton = getBooleanDesignerValue(
    getDesignerValue(
      'isShowSaveButton',
      getDesignerValue('showSaveButton', getDesignerValue('showSave', false)),
    ),
  )
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState({
    mdc: '',
    drg: '',
    rw: '',
    adjRw: '',
    los: '',
    ot: '',
    err: '',
    warn: '',
  })
  const [drgJson,setDrgJson] = useState({input:{},output:{}})
  const [error, setError] = useState(null)
  const [isOpen, setIsOpen] = useState(null)
  const [revisionItems, setRevisionItems] = useState([])

  const mapDrgResult = (raw = {}) => {
    const mapped = {}

    Object.entries(DRG_RESULT_MAPPING).forEach(([from, to]) => {
      mapped[to] = raw?.[from] ?? ''
    })

    return mapped
  }

  const saveDrgResult = async (
    saveResult = result,
    outputJson = drgJson.output,
  ) => {
    const payload = { an: an, hn: hn, ...saveResult }
    await httpClient.put(`/ipd/in-patient-admit/drg/result/${an}`, payload)

    const historyPayload = {
      an: an,
      hn: hn,
      ...saveResult,
      inputJson: null,
      outputJson: outputJson,
      isCurrent: 1,
    }
    await httpClient.post('/ipd/in-patient-admit/drg/revision', historyPayload)
  }

  useEffect(async () => {
    const result = (
      await httpClient.get(`/ipd/in-patient-admit/drg/result/${an}`)
    ).data

    if (result.data) setResult({ err: '', warn: '', ...result.data })
  }, [an])

  useEffect(async () => {
    if (isOpen) {
      const res = await httpClient
        .get(`/ipd/in-patient-admit/drg/revision/${an}`)
        .then(({ data }) => data.data || [])

      setRevisionItems(res || [])
    }
  }, [isOpen])

  const handleCalculate = async () => {
    setLoading(true)
    setError(null)

    const resp = (
      await httpClient.get(`/ipd/in-patient-admit/drg/prepare/${an}`)
    ).data

    // mock input (ปกติคุณจะดึงจาก IPD form)
    const { payload, drgConfig } = resp.data
    let requestUrl = drgConfig?.requestUrl || 'http://localhost:5985'
    try {
      const res = await fetch(`${requestUrl}/drg/${drgConfig?.drgVersion}/sync`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify([payload]),
      })

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }

      const json = await res.json()
      const outputJson = json?.data?.[0] || {}
      const nextResult = mapDrgResult(outputJson)

      setDrgJson({ input: payload, output: outputJson })
      setResult(nextResult)

      if (!isSameDrgResult(result, nextResult)) {
        await saveDrgResult(nextResult, outputJson)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field) => (e) => {
    this.setState({ [field]: e.target.value })
  }

  const handleCompareRevision = () => {
    setIsOpen(true)
  }

  const onSave = async () => {
    try {
      setError(null)
      await saveDrgResult()
    } catch (err) {
      setError(err.message)
    }
  }

  const { mdc, drg, rw, adjRw, los, ot, err, warn } = result
  const Header = medicalStatisticLayout ? 'div' : Card.Header

  return (
    <>
      <Card className="mb-3">
        <Header className={medicalStatisticLayout ? medicalStatisticStyles.sectionHeader : undefined}>
          <Row className={`align-items-center g-2 d-flex justify-content-between${medicalStatisticLayout ? ' w-100' : ''}`}>
            <Col xs={12} md="6">
              {medicalStatisticLayout ? (
                <h5 className="font-weight-bold mb-0">Information</h5>
              ) : (
                <strong>Information</strong>
              )}
            </Col>

            <Col xs={12} md="6" className="ms-md-auto">
              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end">
                <Button
                  variant="primary"
                  className="me-2 mr-1 mb-1"
                  onClick={handleCalculate}
                  disabled={loading}
                >
                  <i className="fa fa-calculator mr-1" />
                  {loading ? 'Calculating...' : 'คำนวณ DRGs'}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleCompareRevision}
                  className="ml-1 mb-1"
                >
                  <i className="fa fa-history" /> Compare Revision
                </Button>
              </div>
            </Col>
          </Row>
        </Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>MDC</Form.Label>
                <Form.Control value={mdc} readOnly />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>DRGs</Form.Label>
                <Form.Control value={drg} readOnly />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>R.W.</Form.Label>
                <Form.Control value={rw} readOnly />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>adjRW.</Form.Label>
                <Form.Control value={adjRw} readOnly />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>LOS</Form.Label>
                <Form.Control value={los} readOnly />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>O.T.</Form.Label>
                <Form.Control value={ot} readOnly />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Err.</Form.Label>
                <Form.Control
                  value={err + (err > 0 ? ' - ' + getErrorDesc(err) : '')}
                  readOnly
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Warn</Form.Label>
                <Form.Control
                  value={warn + (warn > 0 ? ' - ' + getWarningDesc(warn) : '')}
                  readOnly
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
        {isShowSaveButton && (
          <Card.Footer>
            <div className="d-flex justify-content-end">
              <Button
                variant="outline-secondary"
                onClick={onSave}
                className="ml-1"
              >
                <i className="fa fa-save" /> Save
              </Button>
            </div>
          </Card.Footer>
        )}
      </Card>

      <CompareModal
        show={isOpen}
        onHide={() => setIsOpen(false)}
        data={revisionItems || []}
      />
    </>
  )
}

DrgCalculator.defaultProps = {
  designerData: {
    hn: '',
    an: '',
    vn: '',
    isShowSaveButton: 'false',
  },
}

export default DrgCalculator
